import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ProtectedContentComponent } from '../../../organisms/protected-content/protected-content.component';
import { NavigationService } from '../../../../services/navigation.service';
import { AppManagementService } from '../../../../services/app-management/app-management.service';
import { OrganizationService } from '../../../../services/organizations/organization.service';
import { ConfigService } from '../../../../services/config/config.service';
import {
  DeploymentState,
  ManagedApp
} from '../../../../services/app-management/app-management.model';
import {
  ButtonComponent,
  StatusIndicatorComponent,
  StatusIndicatorStatus,
  LoadingDotsComponent,
  TooltipDirective,
  SkeletonComponent
} from 'htx-ui-library';
import {
  Subject,
  catchError,
  filter,
  map,
  merge,
  of,
  switchMap,
  takeWhile,
  tap,
  timer
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../../../organisms/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-app-management-page',
  imports: [
    CommonModule,
    TranslocoDirective,
    ProtectedContentComponent,
    ButtonComponent,
    StatusIndicatorComponent,
    LoadingDotsComponent,
    TooltipDirective,
    SkeletonComponent
  ],
  templateUrl: './app-management-page.component.html',
  styleUrl: './app-management-page.component.scss'
})
export class AppManagementPageComponent {
  private navigationService = inject(NavigationService);
  private appManagementService = inject(AppManagementService);
  private organizationService = inject(OrganizationService);
  private configService = inject(ConfigService);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);

  requiredRoles = this.navigationService.getAccessRoles('app-management');

  readonly isRancherEnv = this.configService.isRancherEnvironment();

  /** Current organization, loaded once */
  private organizationId = toSignal(
    this.organizationService.getMyOrganization().pipe(catchError(() => of(undefined)))
  );

  /** Emits the org ID once it becomes available */
  private orgId$ = toObservable(this.organizationId).pipe(
    filter((org) => !!org?.id),
    map((org) => org!.id)
  );

  /** Manual refresh trigger for deployment status */
  private refreshDeployment$ = new Subject<void>();

  /** Manual refresh trigger for apps list */
  private refreshApps$ = new Subject<void>();

  /** Apps being deployed optimistically (user clicked Install, awaiting backend confirmation) */
  private deployingApps = signal<Set<string>>(new Set());

  /** Apps being undeployed optimistically (user clicked Uninstall, awaiting backend confirmation) */
  private undeployingApps = signal<Set<string>>(new Set());

  /** Track loading state per app (HTTP call in flight) */
  loadingApps = signal<Set<string>>(new Set());

  /** True until the first apps response has arrived (loading vs. genuinely empty) */
  private appsLoaded = signal(false);

  /**
   * Apps list — polled every 10 s on refresh trigger.
   * Polling automatically stops once no app transition is in flight.
   * Restarts when refreshApps$ emits (e.g. after Install/Uninstall).
   */
  apps = toSignal(
    this.orgId$.pipe(
      switchMap((orgId) =>
        merge(of(undefined as void), this.refreshApps$).pipe(
          switchMap(() =>
            timer(0, 10_000).pipe(
              switchMap(() =>
                this.appManagementService
                  .getApps(orgId)
                  .pipe(catchError(() => of([] as ManagedApp[])))
              ),
              tap((rawApps) => {
                this.cleanupTransitionSignals(rawApps);
                this.appsLoaded.set(true);
              }),
              takeWhile(
                (rawApps) =>
                  rawApps.some((a) => a.deploymentState === 'DEPLOYING') ||
                  this.loadingApps().size > 0 ||
                  this.deployingApps().size > 0 ||
                  this.undeployingApps().size > 0,
                true
              )
            )
          )
        )
      )
    ),
    { initialValue: [] as ManagedApp[] }
  );

  /**
   * Deployment (connector) status — polled every 10 s.
   * Polling stops once connector is ACTIVE + Healthy + Synced AND no app transition is in flight.
   * Restarts when refreshDeployment$ emits.
   */
  private deploymentStatus = toSignal(
    this.orgId$.pipe(
      switchMap((orgId) =>
        merge(of(undefined as void), this.refreshDeployment$).pipe(
          switchMap(() =>
            timer(0, 10_000).pipe(
              switchMap(() =>
                this.appManagementService
                  .getDeploymentStatus(orgId)
                  .pipe(catchError(() => of(null)))
              ),
              takeWhile(
                (s) =>
                  s === null ||
                  s.state === 'PROVISIONING' ||
                  s.syncStatus !== 'Synced' ||
                  s.healthStatus !== 'Healthy' ||
                  this.apps().some((a) => a.deploymentState === 'DEPLOYING') ||
                  this.loadingApps().size > 0 ||
                  this.deployingApps().size > 0 ||
                  this.undeployingApps().size > 0,
                true
              )
            )
          )
        )
      )
    )
  );

  /** True once orgId is known and deployment status has been loaded (null = no deployment) */
  hasDeployment = computed(() => this.deploymentStatus() != null);

  /** True while deployment status is still being loaded (undefined = not yet resolved) */
  isDeploymentLoading = computed(() => this.deploymentStatus() === undefined);

  /** True until the apps list has been loaded at least once */
  isAppsLoading = computed(() => !this.appsLoaded());

  /** Combined connector display data */
  connector = computed(() => {
    const deployment = this.deploymentStatus();
    if (!deployment) return undefined;
    return {
      id: deployment.organizationId,
      name: deployment.tenantName,
      status: (deployment.healthStatus === 'Healthy' ? 'UP' : 'DOWN') as StatusIndicatorStatus,
      state: deployment.state,
      managementUrl: deployment.managementUrl,
      syncStatus: deployment.syncStatus,
      healthStatus: deployment.healthStatus
    };
  });

  /** Connector Management App tile — shown only when connector is ACTIVE */
  connectorManagementApp = computed(() => {
    const conn = this.connector();
    if (!conn || conn.state !== 'ACTIVE' || !conn.managementUrl) return undefined;
    return { managementUrl: conn.managementUrl };
  });

  /** True when connector is fully running (for simplified status display) */
  connectorIsOnline = computed(() => {
    const conn = this.connector();
    return (
      conn?.state === 'ACTIVE' && conn?.healthStatus === 'Healthy' && conn?.syncStatus === 'Synced'
    );
  });

  /** True when a use-case app is currently being installed */
  isAppInstallInProgress = computed(
    () =>
      this.deployingApps().size > 0 || this.apps().some((a) => a.deploymentState === 'DEPLOYING')
  );

  /** True when a use-case app is currently being uninstalled */
  isAppUninstallInProgress = computed(() => this.undeployingApps().size > 0);

  /** True when any app transition is happening — suppresses transient ArgoCD error badges */
  private isAppTransitionInProgress = computed(
    () =>
      this.isAppInstallInProgress() ||
      this.isAppUninstallInProgress() ||
      this.loadingApps().size > 0
  );

  /** Tooltip text for the ONLINE status indicator (technical details) */
  connectorTooltip = computed(() => {
    const conn = this.connector();
    if (!conn) return '';
    const stateLine =
      this.translocoService.translate('appManagement.connector.tooltip.state') +
      ': ' +
      this.translocoService.translate('appManagement.connector.state.' + conn.state);
    const deploymentLine =
      this.translocoService.translate('appManagement.connector.tooltip.deployment') +
      ': ' +
      this.translocoService.translate(
        'appManagement.connector.deploymentStatus.' +
          this.deriveDeploymentStatus(conn.healthStatus, conn.syncStatus)
      );
    return [stateLine, deploymentLine].join('\n');
  });

  /**
   * Worst-case mapping of ArgoCD health + sync into a single deployment label.
   * - Healthy + Synced               → READY
   * - Degraded | Missing             → FAILED
   * - Suspended                      → SUSPENDED
   * - Progressing or OutOfSync (no error) → PROVISIONING
   * - otherwise                      → UNKNOWN
   */
  private deriveDeploymentStatus(
    health: string | undefined,
    sync: string | undefined
  ): 'READY' | 'PROVISIONING' | 'FAILED' | 'SUSPENDED' | 'UNKNOWN' {
    if (health === 'Degraded' || health === 'Missing') return 'FAILED';
    if (health === 'Suspended') return 'SUSPENDED';
    if (health === 'Healthy' && sync === 'Synced') return 'READY';
    if (health === 'Progressing' || sync === 'OutOfSync') return 'PROVISIONING';
    return 'UNKNOWN';
  }

  /**
   * Apps with computed deployment state:
   * - NOT_DEPLOYED + user just clicked Install          → DEPLOYING (optimistic)
   * - DEPLOYED + Healthy + online===true                → DEPLOYED
   * - DEPLOYED + Healthy + online===false               → DEPLOYING (ingress not yet ready)
   * - DEPLOYED + Degraded or Missing + no transition    → ERROR (red border, buttons hidden)
   * - DEPLOYED + Degraded or Missing + transition       → DEPLOYING (suppress transient ArgoCD errors)
   * - DEPLOYED + anything else (Progressing/Unknown)    → DEPLOYING
   * NOT_DEPLOYED apps without a pending install are shown as-is.
   */
  computedApps = computed((): ManagedApp[] => {
    const rawApps = this.apps();
    const deployment = this.deploymentStatus();
    const deployingApps = this.deployingApps();
    const isTransitioning = this.isAppTransitionInProgress();

    return rawApps.map((app) => {
      if (app.deploymentState === 'NOT_DEPLOYED' && deployingApps.has(app.id)) {
        return { ...app, deploymentState: 'DEPLOYING' as DeploymentState };
      }

      if (!deployment || app.deploymentState !== 'DEPLOYED') return app;

      const healthStatus = deployment.healthStatus;
      let effectiveState: DeploymentState;

      if (healthStatus === 'Healthy') {
        effectiveState = app.online ? 'DEPLOYED' : 'DEPLOYING';
      } else if ((healthStatus === 'Degraded' || healthStatus === 'Missing') && !isTransitioning) {
        effectiveState = 'ERROR';
      } else {
        effectiveState = 'DEPLOYING';
      }

      return { ...app, deploymentState: effectiveState };
    }).sort((a, b) => {
      const aWeight = a.deploymentState === 'NOT_DEPLOYED' ? 1 : 0;
      const bWeight = b.deploymentState === 'NOT_DEPLOYED' ? 1 : 0;
      if (aWeight !== bWeight) return aWeight - bWeight;
      return this.translocoService
        .translate(a.nameKey)
        .localeCompare(this.translocoService.translate(b.nameKey));
    });
  });

  /** Loading state for connector deploy action */
  isDeployingConnector = signal(false);

  onDeployConnector(): void {
    const orgId = this.organizationId()?.id;
    if (!orgId || this.isDeployingConnector()) return;

    const data: ConfirmationDialogData = {
      title: this.translocoService.translate('appManagement.connector.notDeployed.confirmTitle'),
      message: this.translocoService.translate(
        'appManagement.connector.notDeployed.confirmMessage'
      ),
      confirmText: this.translocoService.translate(
        'appManagement.connector.notDeployed.confirmButton'
      ),
      cancelText: this.translocoService.translate(
        'appManagement.connector.notDeployed.cancelButton'
      ),
      variant: 'success',
      iconVariant: 'primary',
      icon: 'rocket_launch'
    };

    this.dialog
      .open(ConfirmationDialogComponent, {
        data,
        width: '480px',
        autoFocus: false
      })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => {
        this.isDeployingConnector.set(true);
        this.appManagementService.provisionConnector(orgId).subscribe({
          next: () => {
            this.isDeployingConnector.set(false);
            this.refreshDeployment$.next();
          },
          error: () => this.isDeployingConnector.set(false)
        });
      });
  }

  onDeploy(app: ManagedApp): void {
    const orgId = this.organizationId()?.id;
    if (!orgId || this.isLoading(app.id)) return;
    this.addDeployingApp(app.id);
    this.setLoading(app.id, true);
    this.appManagementService.enableApp(orgId, app.id).subscribe({
      next: () => {
        this.setLoading(app.id, false);
        this.refreshApps$.next();
        this.refreshDeployment$.next();
      },
      error: () => {
        this.setLoading(app.id, false);
        this.removeDeployingApp(app.id);
      }
    });
  }

  onUndeploy(app: ManagedApp): void {
    const orgId = this.organizationId()?.id;
    if (!orgId || this.isLoading(app.id)) return;

    const appName = this.translocoService.translate(app.nameKey);
    const data: ConfirmationDialogData = {
      title: `"${appName}" ${this.translocoService.translate('appManagement.apps.undeployDialog.titleAction')}`,
      message: this.translocoService.translate('appManagement.apps.undeployDialog.message'),
      confirmText: this.translocoService.translate('appManagement.apps.undeployDialog.confirmText'),
      cancelText: this.translocoService.translate('appManagement.apps.undeployDialog.cancelText'),
      checkboxLabel: this.translocoService.translate(
        'appManagement.apps.undeployDialog.checkboxLabel'
      )
    };

    this.dialog
      .open(ConfirmationDialogComponent, {
        data,
        width: '480px',
        autoFocus: false
      })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => {
        this.addUndeployingApp(app.id);
        this.setLoading(app.id, true);
        this.appManagementService.disableApp(orgId, app.id).subscribe({
          next: () => {
            this.setLoading(app.id, false);
            this.removeDeployingApp(app.id);
            this.refreshApps$.next();
            this.refreshDeployment$.next();
          },
          error: () => {
            this.setLoading(app.id, false);
            this.removeUndeployingApp(app.id);
          }
        });
      });
  }

  onOpenApp(app: ManagedApp): void {
    if (app.appUrl) {
      window.open(app.appUrl, '_blank');
    }
  }

  onOpenConnectorManagement(): void {
    const conn = this.connector();
    if (conn?.managementUrl) {
      window.open(conn.managementUrl, '_blank');
    }
  }

  isLoading(appId: string): boolean {
    return this.loadingApps().has(appId);
  }

  private setLoading(appId: string, loading: boolean): void {
    const current = new Set(this.loadingApps());
    if (loading) {
      current.add(appId);
    } else {
      current.delete(appId);
    }
    this.loadingApps.set(current);
  }

  private addDeployingApp(appId: string): void {
    this.deployingApps.update((s) => new Set([...s, appId]));
  }

  private removeDeployingApp(appId: string): void {
    this.deployingApps.update((s) => {
      const next = new Set(s);
      next.delete(appId);
      return next;
    });
  }

  private addUndeployingApp(appId: string): void {
    this.undeployingApps.update((s) => new Set([...s, appId]));
  }

  private removeUndeployingApp(appId: string): void {
    this.undeployingApps.update((s) => {
      const next = new Set(s);
      next.delete(appId);
      return next;
    });
  }

  /** Clean up optimistic transition signals once backend confirms final state */
  private cleanupTransitionSignals(rawApps: ManagedApp[]): void {
    const deploying = this.deployingApps();
    if (deploying.size > 0) {
      const cleaned = new Set(deploying);
      for (const appId of deploying) {
        const raw = rawApps.find((a) => a.id === appId);
        if (raw && raw.deploymentState === 'DEPLOYED' && raw.online) {
          cleaned.delete(appId);
        }
      }
      if (cleaned.size !== deploying.size) this.deployingApps.set(cleaned);
    }

    const undeploying = this.undeployingApps();
    if (undeploying.size > 0) {
      const cleaned = new Set(undeploying);
      for (const appId of undeploying) {
        const raw = rawApps.find((a) => a.id === appId);
        if (raw && raw.deploymentState === 'NOT_DEPLOYED') {
          cleaned.delete(appId);
        }
      }
      if (cleaned.size !== undeploying.size) this.undeployingApps.set(cleaned);
    }
  }
}
